/**
 * stripe-connect-api — typed wrappers for the ADMIN Stripe Connect ops
 * (stripe-fe handoff 2026-07-23; backend merged to voyage dev, PRs #149/#153),
 * through the @v0y4ge/api-client `apiRaw` choke point (paypal-connect-api /
 * adyen-connect-api pattern — the handoff's raw-fetch shadow API layer does not
 * exist in this tree and raw fetch is a drop-reject). Declared REQ-064; served
 * by the harness `__STRIPE_SEED_LAYER__` until they land in @v0y4ge/api-client
 * / api-mocks.
 *
 * ENVELOPE (fix 2026-07-24): the real backend wraps responses as `{ data: {…} }`
 * (same as catalog/detail and the generic verifyIntegration, which reads
 * `res.data.ok`), and `apiRaw` returns the RAW body — it does NOT unwrap. So the
 * status/activity/verify readers unwrap `.data` here (falling back to the bare
 * body so a future un-enveloped seed/mock still works). Without this, real `dev`
 * data reads as not-connected / empty / unhealthy while the preview seed masks it.
 *
 * Degrade-gracefully contract (handoff docket, design-time §): status → null
 * when the backend has no connection history (404) or the route is unserved;
 * activity → [] — so the screen renders the clean not-connected state and
 * never crashes in mock mode.
 */
import { apiRaw, ApiError } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

// Unwrap the backend's `{ data: … }` envelope; fall back to the bare body.
const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;

export type StripeCatalogStatus = "connected" | "disconnected" | "needs-attention" | "coming-soon";

export interface StripeConnection {
  account_id: string | null;
  publishable_key: string | null;
  account_country: string | null;
  livemode: boolean;
  last_verified_at: string | null;
  last_error: string | null;
}

export interface StripeStatusResponse {
  provider: string;
  status: StripeCatalogStatus;
  connection: StripeConnection | null;
  latest_verification: unknown | null;
}

export interface StripeVerifyResult {
  ok: boolean;
  account: { id?: string; label?: string; email?: string } | null;
  error?: { code: string; message: string; retriable?: boolean };
  warnings?: string[];
}

export interface StripeActivityItem {
  id: string;
  kind: string;
  occurred_at: string;
  detail: string;
  outcome: "success" | "failure";
}

/** Live connection status. Returns null when the backend has no connection/
 *  verification history yet (404), so callers render not-connected without
 *  treating it as an error. Unwraps the `{ data }` envelope. */
export async function getStripeStatus(): Promise<StripeStatusResponse | null> {
  try {
    const body = await tag("getStripeStatus", apiRaw<{ data?: StripeStatusResponse } | StripeStatusResponse>("/v1/admin/integrations/stripe/status"));
    return unwrap<StripeStatusResponse>(body);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/** Recent-activity feed (connection + verification events, newest first).
 *  Never throws — [] on any failure (docket degrade contract). Unwraps the
 *  `{ data: { items } }` envelope. */
export async function getStripeActivity(): Promise<StripeActivityItem[]> {
  try {
    const body = await tag("getStripeActivity", apiRaw<
      { data?: { items?: StripeActivityItem[] } | StripeActivityItem[] } | { items?: StripeActivityItem[] } | StripeActivityItem[]
    >("/v1/admin/integrations/stripe/activity"));
    const d = Array.isArray(body) ? body : unwrap<{ items?: StripeActivityItem[] } | StripeActivityItem[]>(body);
    return Array.isArray(d) ? d : (d?.items ?? []);
  } catch { return []; }
}

/** Real live verify via the central broker (was a hardcoded "healthy" toast
 *  pre-handoff). Always resolves with { ok, ... } — read ok/error, not HTTP.
 *  Unwraps the `{ data }` envelope. */
export const verifyStripe = (): Promise<StripeVerifyResult> =>
  tag("verifyStripe", apiRaw<{ data?: StripeVerifyResult } | StripeVerifyResult>("/v1/admin/integrations/stripe/verify", { method: "POST" }, 'verifyStripe')
    .then((body) => unwrap<StripeVerifyResult>(body)));

/** OAuth-namespaced disconnect (…/connect/disconnect) — symmetric with
 *  connect/start; NOT the plain /{provider}/disconnect used by API-key
 *  providers (docket §4). Response body is not read (caller refetches status). */
export const disconnectStripe = () =>
  tag("disconnectStripe", apiRaw<{ ok?: boolean }>("/v1/admin/integrations/stripe/connect/disconnect", { method: "POST" }, 'disconnectStripe'));

/**
 * Begin Stripe Connect. UNIFORM with startPayPalConnect / startAdyenOnboarding
 * (fix 2026-07-24): the backend mints the authorize URL and returns it as JSON
 * `{ url }`; the browser navigates to it via `window.location.assign` (the FE
 * never builds the URL). The real backend's `connect/start` is now a POST
 * returning `{ url }` (was a browser 302 — a redirect that could not round-trip
 * a fetch mock and left the CD preview stuck). Real: `url` is Stripe's OAuth
 * consent page, returning to `redirect_to` with ?stripe=connected. Preview: the
 * mock returns the integration-detail route and persists the connection across
 * the navigation reload. No preview flag, no full-page navigation to the API
 * endpoint — those were the "stuck in Claude Design" failure modes.
 */
export const startStripeConnect = (redirectTo: string) =>
  tag("startStripeConnect", apiRaw<{ url: string }>(
    `/v1/admin/integrations/stripe/connect/start?redirect_to=${encodeURIComponent(redirectTo)}`,
    { method: "POST" }, 'startStripeConnect'));
